/**
 * Premium Assignment Results Display
 * World-class UI with PDF download, competency scoring, and AI analysis
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Slider
} from '@mui/material';
import {
  Close,
  Person,
  Download,
  Print,
  ExpandMore,
  CheckCircle,
  Cancel,
  TrendingUp,
  EmojiEvents,
  School,
  AudioFile,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeDown,
  Replay,
  MenuBook,
  RecordVoiceOver,
  Hearing
} from '@mui/icons-material';
import { apiUtils, API_ENDPOINTS } from '../config/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface AssignmentResultsProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  assignmentData?: any;
}

// Competency Configuration
const COMPETENCY_CONFIG: any = {
  pronunciation: {
    label: 'Pronunciation',
    icon: '🗣️',
    color: '#3b82f6',
    lightBg: '#eff6ff',
    description: 'Speech clarity and accuracy'
  },
  fluency: {
    label: 'Fluency',
    icon: '💫',
    color: '#8b5cf6',
    lightBg: '#f5f3ff',
    description: 'Flow and naturalness'
  },
  vocabulary: {
    label: 'Vocabulary',
    icon: '📚',
    color: '#10b981',
    lightBg: '#ecfdf5',
    description: 'Word choice and range'
  },
  articulation: {
    label: 'Articulation',
    icon: '🔗',
    color: '#f59e0b',
    lightBg: '#fffbeb',
    description: 'Sentence structure'
  },
  grammar: {
    label: 'Grammar',
    icon: '✅',
    color: '#06b6d4',
    lightBg: '#ecfeff',
    description: 'Grammatical accuracy'
  },
  spoken_english_understanding: {
    label: 'Comprehension',
    icon: '🎯',
    color: '#ec4899',
    lightBg: '#fdf2f8',
    description: 'Understanding ability'
  },
  active_listening: {
    label: 'Active Listening',
    icon: '👂',
    color: '#f43f5e',
    lightBg: '#fff1f2',
    description: 'Listening skills'
  }
};

// Circular Progress Component
const CircularProgressWithLabel: React.FC<{
  value: number;
  size?: number;
  color?: string;
}> = ({ value, size = 80, color = '#3b82f6' }) => {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={5}
        sx={{
          color: color,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 700, color: color }}
        >
          {Math.round(value)}
        </Typography>
      </Box>
    </Box>
  );
};

// Audio Player Component for answer recordings
const AudioPlayer: React.FC<{ audioUrl: string; questionText?: string }> = ({ audioUrl, questionText }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Create proxy URL for better CORS handling
  const getProxyUrl = (originalUrl: string) => {
    if (originalUrl.includes('gappeostorage.blob.core.windows.net')) {
      const baseUrl = window.location.origin.replace(':3000', ':8000');
      return `${baseUrl}/api/v1/audio-proxy/proxy?url=${encodeURIComponent(originalUrl)}`;
    }
    return originalUrl;
  };

  const proxyUrl = getProxyUrl(audioUrl);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      setHasError(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setHasError(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      setErrorMessage('Unable to load audio. The file may be private or temporarily unavailable.');
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setHasError(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [proxyUrl]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => {
        setHasError(true);
        setErrorMessage('Playback failed. The audio file may not be accessible.');
      });
      setIsPlaying(true);
    }
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    const seekTime = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.volume = newVolume;
    setVolume(newVolume);
  };

  const handleReplay = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    audio.currentTime = 0;
    setCurrentTime(0);
    if (!isPlaying) {
      audio.play().catch(() => {
        setHasError(true);
        setErrorMessage('Replay failed. The audio file may not be accessible.');
      });
      setIsPlaying(true);
    }
  };

  const handleDirectDownload = () => {
    window.open(proxyUrl, '_blank');
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Paper sx={{ p: 2, mt: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <audio
        ref={audioRef}
        src={proxyUrl}
        preload="metadata"
      />

      {questionText && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <MenuBook fontSize="small" />
            Text to Read:
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            "{questionText}"
          </Typography>
        </Box>
      )}

      {hasError && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350' }}>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            ⚠️ Audio Loading Error
          </Typography>
          <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleDirectDownload}
              startIcon={<AudioFile />}
            >
              Try Direct Link
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={() => window.open(audioUrl, '_blank')}
            >
              Original URL
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title={hasError ? "Audio unavailable" : isPlaying ? "Pause" : "Play"}>
          <IconButton
            onClick={togglePlayback}
            disabled={isLoading || hasError}
            aria-label={hasError ? "Audio unavailable" : isPlaying ? "Pause audio" : "Play audio"}
            sx={{
              bgcolor: hasError ? '#ef5350' : '#667eea',
              color: 'white',
              '&:hover': { bgcolor: hasError ? '#e53935' : '#5568d3' },
              '&:disabled': { bgcolor: '#bdbdbd' }
            }}
          >
            {isLoading ? (
              <AudioFile />
            ) : hasError ? (
              <Cancel />
            ) : isPlaying ? (
              <Pause />
            ) : (
              <PlayArrow />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Replay from start">
          <IconButton onClick={handleReplay} disabled={isLoading || hasError} aria-label="Replay audio from start">
            <Replay />
          </IconButton>
        </Tooltip>

        <Tooltip title="Download audio file">
          <IconButton onClick={handleDirectDownload} size="small" aria-label="Download audio file">
            <AudioFile />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1, mx: 2 }}>
          <Slider
            value={currentTime}
            max={duration || 100}
            onChange={handleSeek}
            disabled={isLoading || hasError || duration === 0}
            sx={{ color: hasError ? '#ef5350' : '#667eea' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasError ? 'Error' : formatTime(duration)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
          <VolumeDown fontSize="small" />
          <Slider
            value={volume}
            max={1}
            step={0.1}
            onChange={handleVolumeChange}
            disabled={hasError}
            sx={{ width: 60, color: hasError ? '#ef5350' : '#667eea' }}
          />
          <VolumeUp fontSize="small" />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
        <RecordVoiceOver fontSize="small" color={hasError ? 'error' : 'primary'} />
        <Typography variant="caption" color="text.secondary">
          Answer Recording • {
            isLoading ? 'Loading...' :
            hasError ? 'Error loading audio' :
            duration > 0 ? formatTime(duration) : 'Ready to play'
          }
        </Typography>
      </Box>

      {hasError && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Fallback Audio Player:
          </Typography>
          <audio
            controls
            style={{ width: '100%', maxWidth: '400px' }}
          >
            <source src={proxyUrl} type="audio/mpeg" />
            <source src={audioUrl} type="audio/mpeg" />
            <source src={proxyUrl} type="audio/wav" />
            <source src={proxyUrl} type="audio/mp4" />
            Your browser does not support the audio element.
          </audio>
        </Box>
      )}
    </Paper>
  );
};

// Overall Summary Card Component
const OverallSummaryCard: React.FC<{ summary: any; cefrRating: any }> = ({ summary, cefrRating }) => {
  if (!summary) return null;

  const competencyScores = summary.competency_scores || {};

  return (
    <Paper
      elevation={3}
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 4,
        borderRadius: 3,
        mb: 3
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <EmojiEvents sx={{ fontSize: 40, mr: 2 }} />
        <Typography variant="h5" fontWeight={700}>
          Overall Assessment Summary
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)', mb: 3 }} />

      {/* AI Generated Summary */}
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, opacity: 0.95 }}>
        {summary.overall_summary || 'Assessment summary not available.'}
      </Typography>

      {/* CEFR Justification */}
      {summary.cefr_justification && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <School /> CEFR {cefrRating?.level} Justification:
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {summary.cefr_justification}
          </Typography>
        </Box>
      )}

      {/* Competency Scores Grid */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          📊 Competency Breakdown
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(competencyScores).map(([key, data]: [string, any]) => {
            if (!data.assessed) return null;
            const config = COMPETENCY_CONFIG[key];
            if (!config) return null;

            return (
              <Box key={key} sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 180 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    height: '100%'
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <CircularProgressWithLabel
                      value={data.score || 0}
                      size={60}
                      color={config.color}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '1.2rem', display: 'block', mb: 0.5 }}>
                    {config.icon}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {config.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.score}/100
                  </Typography>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Strengths and Improvements */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
        {summary.strengths && summary.strengths.length > 0 && (
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 300 }}>
            <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.2)', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                💪 Key Strengths
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {summary.strengths.map((strength: string, idx: number) => (
                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5 }}>
                    {strength}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {summary.areas_for_improvement && summary.areas_for_improvement.length > 0 && (
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 300 }}>
            <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.2)', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📈 Growth Opportunities
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {summary.areas_for_improvement.map((item: string, idx: number) => (
                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Key Observations */}
      {summary.key_observations && summary.key_observations.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            🔍 Key Observations
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {summary.key_observations.map((obs: string, idx: number) => (
              <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5 }}>
                {obs}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// Question Analysis Card Component (formatted, no debug boxes)
const QuestionAnalysisCard: React.FC<{
  question: any;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}> = ({ question, index, expanded, onToggle }) => {

  // Parse analysis text if it has ** markers
  const parseAnalysisText = (text: string) => {
    if (!text) return { main: '', sections: [] };

    const parts = text.split(/\*\*([^*]+):\*\*/);
    const main = parts[0]?.trim() || '';
    const sections: any[] = [];

    for (let i = 1; i < parts.length; i += 2) {
      const title = parts[i]?.trim();
      const content = parts[i + 1]?.trim();
      if (title && content) {
        sections.push({ title, content });
      }
    }

    return { main, sections };
  };

  const analysis = question.analysis?.analysis || '';
  const parsed = parseAnalysisText(analysis);

  const questionPercentage = question.max_score > 0
    ? (question.score / question.max_score) * 100
    : 0;

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return '#4caf50';
    if (percentage >= 60) return '#ff9800';
    return '#f44336';
  };

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: expanded ? 'grey.50' : 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
        onClick={onToggle}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Q{index + 1}: {question.question_text?.substring(0, 100)}
            {question.question_text?.length > 100 && '...'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {question.question_type?.replace(/_/g, ' ')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: getScoreColor(questionPercentage) }}>
              {question.score?.toFixed(1)}/{question.max_score}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={questionPercentage}
              sx={{
                width: 80,
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getScoreColor(questionPercentage)
                }
              }}
            />
          </Box>

          {question.is_correct === true ? (
            <CheckCircle sx={{ color: '#4caf50', fontSize: 28 }} />
          ) : question.is_correct === false ? (
            <Cancel sx={{ color: '#f44336', fontSize: 28 }} />
          ) : null}

          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              aria-label={expanded ? 'Collapse question details' : 'Expand question details'}
              aria-expanded={expanded}
              sx={{
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
              }}
            >
              <ExpandMore
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expanded Details */}
      {expanded && (
        <Box sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
          {/* Audio Player for speaking/audio questions */}
          {question.audio_url && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AudioFile color="primary" fontSize="small" />
                Student's Response Recording:
              </Typography>
              <audio controls style={{ width: '100%' }}>
                <source src={question.audio_url} type="audio/mpeg" />
                <source src={question.audio_url} type="audio/wav" />
                <source src={question.audio_url} type="audio/mp4" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}

          {/* MCQ Options for multiple choice questions */}
          {question.question_type === 'multiple_choice' && (
            <MCQOptionsDisplay question={question} />
          )}

          {/* Main Analysis */}
          {parsed.main && (
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
              {parsed.main}
            </Typography>
          )}

          {/* Competency Sections */}
          {parsed.sections.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              {parsed.sections.map((section, idx) => {
                const competencyKey = Object.keys(COMPETENCY_CONFIG).find(key =>
                  section.title.toLowerCase().includes(key.replace(/_/g, ' '))
                );
                const config = competencyKey ? COMPETENCY_CONFIG[competencyKey] : null;

                return (
                  <Box key={idx} sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 300 }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: config?.color || '#666',
                        bgcolor: config?.lightBg || '#f5f5f5'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {config?.icon && (
                          <Typography variant="h6">{config.icon}</Typography>
                        )}
                        <Typography variant="subtitle2" fontWeight={600}>
                          {section.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {section.content}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Individual Scores (for speaking questions) */}
          {question.analysis?.individual_scores && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Skill Breakdown:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(question.analysis.individual_scores).map(([skill, score]: [string, any]) => {
                  if (!skill.includes('score')) return null;
                  const skillName = skill.replace('_score', '').replace(/_/g, ' ');
                  const percentage = (score / 10) * 100;

                  return (
                    <Chip
                      key={skill}
                      label={`${skillName}: ${score}/10`}
                      size="small"
                      sx={{
                        bgcolor: percentage >= 80 ? '#4caf50' : percentage >= 60 ? '#ff9800' : '#f44336',
                        color: 'white',
                        textTransform: 'capitalize',
                        fontWeight: 600
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Audio Player for answer recordings */}
          {question.answer_url && (
            <AudioPlayer
              audioUrl={question.answer_url}
              questionText={question.question_type === 'reading_fluency' ? question.question_text : undefined}
            />
          )}
        </Box>
      )}
    </Card>
  );
};

// Helper function to generate MCQ options from question content
const generateMCQOptions = (question: any) => {
  const content = question.content || {};

  console.log('🔍 [MCQ DEBUG] Generating options for question:', {
    question_id: question.assignment_question_id,
    question_type: question.question_type,
    content: content,
    content_keys: Object.keys(content),
    full_question: question
  });

  // Try to get options from 'options' array first
  if (content.options && Array.isArray(content.options)) {
    console.log('✅ Found options array:', content.options);
    // Ensure each option has id and text
    return content.options.map((opt: any, idx: number) => {
      if (typeof opt === 'string') {
        return { id: String.fromCharCode(65 + idx), text: opt }; // A, B, C, D
      }
      return { id: opt.id || String.fromCharCode(65 + idx), text: opt.text || opt };
    });
  }

  // Try to construct from option_a, option_b, etc. (lowercase)
  const options = [];
  const optionKeys = ['a', 'b', 'c', 'd'];

  for (const key of optionKeys) {
    const optionKey = `option_${key}`;
    if (content[optionKey]) {
      options.push({
        id: key.toUpperCase(),
        text: content[optionKey]
      });
      console.log(`✅ Found ${optionKey}:`, content[optionKey]);
    }
  }

  if (options.length > 0) {
    console.log('✅ Built options from option_a/b/c/d:', options);
    return options;
  }

  // Try uppercase keys (option_A, option_B)
  const upperOptionKeys = ['A', 'B', 'C', 'D'];
  for (const key of upperOptionKeys) {
    const optionKey = `option_${key}`;
    if (content[optionKey]) {
      options.push({
        id: key,
        text: content[optionKey]
      });
      console.log(`✅ Found ${optionKey}:`, content[optionKey]);
    }
  }

  if (options.length > 0) {
    console.log('✅ Built options from option_A/B/C/D:', options);
    return options;
  }

  // Try without underscore (optionA, optionB)
  for (const key of upperOptionKeys) {
    const optionKey = `option${key}`;
    if (content[optionKey]) {
      options.push({
        id: key,
        text: content[optionKey]
      });
      console.log(`✅ Found ${optionKey}:`, content[optionKey]);
    }
  }

  if (options.length > 0) {
    console.log('✅ Built options from optionA/B/C/D:', options);
    return options;
  }

  // Try choices array (alternative naming)
  if (content.choices && Array.isArray(content.choices)) {
    console.log('✅ Found choices array:', content.choices);
    return content.choices.map((choice: any, idx: number) => ({
      id: String.fromCharCode(65 + idx),
      text: typeof choice === 'string' ? choice : choice.text
    }));
  }

  // If still no options found, return placeholders
  console.warn('⚠️ No options found in content, returning placeholders. Content was:', content);
  return [
    { id: 'A', text: '' },
    { id: 'B', text: '' },
    { id: 'C', text: '' },
    { id: 'D', text: '' }
  ];
};

// MCQ Options Display Component
const MCQOptionsDisplay: React.FC<{ question: any }> = ({ question }) => {
  const options = generateMCQOptions(question);

  console.log('📋 [MCQ DISPLAY] Rendering options:', options);
  console.log('📋 [MCQ DISPLAY] Question:', question);

  // Extract selected answer ID (e.g., "A" from "A) Option text")
  const selectedAnswerId = question.candidate_answer
    ? question.candidate_answer.split(')')[0].trim().toUpperCase()
    : null;

  const correctAnswerId = question.correct_answer?.toUpperCase();

  console.log('📋 [MCQ DISPLAY] Selected:', selectedAnswerId, 'Correct:', correctAnswerId);

  // Return null if no options to prevent rendering empty space
  if (!options || options.length === 0) return null;

  const getOptionStyle = (optionId: string) => {
    const isSelected = selectedAnswerId === optionId;
    const isCorrect = correctAnswerId === optionId;

    if (isSelected && isCorrect) {
      return {
        bgcolor: '#e8f5e9',
        borderColor: '#4caf50',
        borderWidth: 2,
        icon: '✓',
        iconColor: '#4caf50'
      };
    }

    if (isSelected && !isCorrect) {
      return {
        bgcolor: '#ffebee',
        borderColor: '#f44336',
        borderWidth: 2,
        icon: '✗',
        iconColor: '#f44336'
      };
    }

    if (!isSelected && isCorrect) {
      return {
        bgcolor: '#e8f5e9',
        borderColor: '#81c784',
        borderWidth: 2,
        icon: '✓',
        iconColor: '#4caf50'
      };
    }

    return {
      bgcolor: '#f5f5f5',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      icon: '',
      iconColor: ''
    };
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ mb: 2.5 }}>
        Options:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {options.map((option: any) => {
          const style = getOptionStyle(option.id);

          return (
            <Paper
              key={option.id}
              elevation={style.borderWidth === 2 ? 2 : 0}
              sx={{
                p: 2.5,
                bgcolor: style.bgcolor,
                border: `${style.borderWidth}px solid ${style.borderColor}`,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s',
                minHeight: 56
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    minWidth: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    fontSize: '0.875rem'
                  }}
                >
                  {option.id}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: option.text ? 'text.primary' : 'text.disabled',
                    fontStyle: option.text ? 'normal' : 'italic'
                  }}
                >
                  {option.text || `[Option ${option.id} - No text available]`}
                </Typography>
              </Box>
              {style.icon && (
                <Typography
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: style.iconColor,
                    ml: 2
                  }}
                >
                  {style.icon}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

// Parent Question Display Component (for listening/reading comprehension sets)
const ParentQuestionDisplay: React.FC<{
  question: any;
  isExpanded: boolean;
  onToggle: () => void;
  childCount: number;
}> = ({ question, isExpanded, onToggle, childCount }) => {
  const questionType = question.question_type || '';
  const content = question.content || {};

  // For listening comprehension sets - show audio
  if (questionType.includes('listening')) {
    // Try multiple possible audio URL locations
    const audioUrl =
      question.audio_url ||
      content.audio_url ||
      content.question_audio_url ||
      content.audioUrl ||
      (question.submission?.answer?.audio_url);

    console.log('Listening Comprehension - Question:', question);
    console.log('Listening Comprehension - Audio URL:', audioUrl);

    return (
      <Paper sx={{ mb: 2, overflow: 'hidden', border: '2px solid #2196f3', borderRadius: 2 }}>
        {/* Header - Clickable to expand/collapse */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#e3f2fd',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '&:hover': { bgcolor: '#bbdefb' }
          }}
          onClick={onToggle}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Hearing sx={{ fontSize: 32, color: '#2196f3' }} />
            <Box>
              <Typography variant="h6" fontWeight={600} color="primary">
                🎧 Listening Comprehension
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {childCount} {childCount === 1 ? 'question' : 'questions'} based on this audio
              </Typography>
            </Box>
          </Box>
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              aria-label={isExpanded ? 'Collapse listening comprehension' : 'Expand listening comprehension'}
              aria-expanded={isExpanded}
              sx={{
                '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.08)' },
                '&:focus': { outline: '2px solid #2196f3', outlineOffset: 2 }
              }}
            >
              <ExpandMore
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content - Shown when expanded */}
        {isExpanded && (
          <Box sx={{ p: 3, bgcolor: 'white' }}>
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              {question.question_text || 'Listen to this audio paragraph carefully. You will hear it ONCE only, then answer questions.'}
            </Typography>

            {audioUrl ? (
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, border: '1px solid #90caf9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AudioFile fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={500}>Audio Recording:</Typography>
                </Box>
                <audio controls style={{ width: '100%' }}>
                  <source src={audioUrl} type="audio/mpeg" />
                  <source src={audioUrl} type="audio/wav" />
                  <source src={audioUrl} type="audio/mp4" />
                  Your browser does not support the audio element.
                </audio>
              </Box>
            ) : (
              <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 2, border: '1px solid #ff9800' }}>
                <Typography variant="body2" color="text.secondary">
                  ℹ️ Audio recording not available for this question
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  // For reading comprehension sets - show paragraph
  if (questionType.includes('reading')) {
    // Try to get paragraph from content first (the actual text to read)
    const paragraph = content.paragraph || content.text || content.reading_text || question.question_text;
    const instructionText = question.question_text;

    console.log('Reading Comprehension - Question:', question);
    console.log('Reading Comprehension - Paragraph:', paragraph);
    console.log('Reading Comprehension - Content:', content);

    return (
      <Paper sx={{ mb: 2, overflow: 'hidden', border: '2px solid #9c27b0', borderRadius: 2 }}>
        {/* Header - Clickable to expand/collapse */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#f3e5f5',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '&:hover': { bgcolor: '#e1bee7' }
          }}
          onClick={onToggle}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <MenuBook sx={{ fontSize: 32, color: '#9c27b0' }} />
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: '#9c27b0' }}>
                📖 Reading Comprehension
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {childCount} {childCount === 1 ? 'question' : 'questions'} based on this passage
              </Typography>
            </Box>
          </Box>
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              aria-label={isExpanded ? 'Collapse reading comprehension' : 'Expand reading comprehension'}
              aria-expanded={isExpanded}
              sx={{
                '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.08)' },
                '&:focus': { outline: '2px solid #9c27b0', outlineOffset: 2 }
              }}
            >
              <ExpandMore
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content - Shown when expanded */}
        {isExpanded && (
          <Box sx={{ p: 3, bgcolor: 'white' }}>
            {/* Show instruction if it's different from paragraph */}
            {instructionText && instructionText !== paragraph && instructionText.length < 100 && (
              <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                {instructionText}
              </Typography>
            )}

            <Paper sx={{ p: 2.5, bgcolor: '#f5f5f5', border: '1px solid #ce93d8', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {paragraph}
              </Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    );
  }

  return null;
};

// Main Component
const PremiumAssignmentResults: React.FC<AssignmentResultsProps> = ({
  open,
  onClose,
  assignmentId,
  assignmentData
}) => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleParentExpanded = (parentId: string) => {
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };

  const expandAllInSection = (sectionQuestions: any[]) => {
    // Expand parent questions
    const parentIds = sectionQuestions
      .filter((q: any) => q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent'))
      .map((q: any) => q.assignment_question_id);
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      parentIds.forEach((id: string) => newSet.add(id));
      return newSet;
    });

    // Expand all individual question cards
    const allQuestionIds = sectionQuestions.map((q: any) => q.assignment_question_id);
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      allQuestionIds.forEach((id: string) => newSet.add(id));
      return newSet;
    });
  };

  const collapseAllInSection = (sectionQuestions: any[]) => {
    // Collapse parent questions
    const parentIds = sectionQuestions
      .filter((q: any) => q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent'))
      .map((q: any) => q.assignment_question_id);
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      parentIds.forEach((id: string) => newSet.delete(id));
      return newSet;
    });

    // Collapse all individual question cards
    const allQuestionIds = sectionQuestions.map((q: any) => q.assignment_question_id);
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      allQuestionIds.forEach((id: string) => newSet.delete(id));
      return newSet;
    });
  };

  const fetchResults = useCallback(async () => {
    if (!assignmentId) return;

    setLoading(true);
    setError(null);

    try {
      const resultsResponse = await apiUtils.get(API_ENDPOINTS.GET_ASSIGNMENT_RESULTS_SIMPLE(assignmentId));

      if (resultsResponse.status === 'success' && resultsResponse.data) {
        setResults(resultsResponse.data);
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (err: any) {
      console.error('Error fetching results:', err);
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    if (open && assignmentId) {
      fetchResults();
    }
  }, [open, assignmentId, fetchResults]);

  // PDF Download Handler
  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Assessment_Results_${results?.assignment?.name || 'Report'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Print Handler
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Results...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !results) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Assessment Results</DialogTitle>
        <DialogContent>
          <Typography color="error">{error || 'No results available'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {error && <Button onClick={fetchResults} variant="contained">Retry</Button>}
        </DialogActions>
      </Dialog>
    );
  }

  const { assignment, sections } = results;
  const overallSummary = assignment.overall_summary;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '95vh' }
      }}
    >
      {/* Header with Action Buttons */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrendingUp sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700}>
            Assessment Results
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download PDF">
            <IconButton onClick={handleDownloadPDF} sx={{ color: 'white' }} aria-label="Download PDF">
              <Download />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint} sx={{ color: 'white' }} aria-label="Print assessment results">
              <Print />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Close assessment results">
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, overflow: 'auto' }} ref={printRef}>
        <Box sx={{ p: 4 }}>
          {/* Candidate Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: '#667eea' }}>
                <Person sx={{ fontSize: 40 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {assignment?.name || 'Candidate'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {assignment?.email}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 3, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {assignment?.overall_score?.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Score</Typography>
              </Box>
              {assignment?.total_score_weighted != null && (
                <Box sx={{ textAlign: 'center', px: 3, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                  <Typography variant="h3" fontWeight={700} sx={{ color: '#0d9488' }}>
                    {Number(assignment.total_score_weighted).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Weighted Score</Typography>
                </Box>
              )}
              <Box sx={{ textAlign: 'center', px: 3, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#667eea' }}>
                  {assignment?.cefr_rating?.level || '--'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {assignment?.cefr_rating?.description || 'CEFR Level'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 3, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {assignment?.percentage?.toFixed(0)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Percentage</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Overall Summary (Gemini AI) */}
          {overallSummary && (
            <OverallSummaryCard summary={overallSummary} cefrRating={assignment.cefr_rating} />
          )}

          {/* Section-wise Questions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 4 }}>
            <Typography variant="h5" fontWeight={700}>
              📋 Detailed Question Analysis
            </Typography>
            <Tooltip title={(() => {
              const allParentIds: string[] = [];
              sections?.forEach((section: any) => {
                section.questions?.forEach((q: any) => {
                  if (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) {
                    allParentIds.push(q.assignment_question_id);
                  }
                });
              });
              const allExpanded = allParentIds.every(id => expandedParents.has(id));
              return allExpanded ? 'Collapse All Sections' : 'Expand All Sections';
            })()}>
              <IconButton
                size="medium"
                onClick={() => {
                  // Get all parent IDs and all question IDs
                  const allParentIds: string[] = [];
                  const allQuestionIds: string[] = [];

                  sections?.forEach((section: any) => {
                    section.questions?.forEach((q: any) => {
                      allQuestionIds.push(q.assignment_question_id);
                      if (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) {
                        allParentIds.push(q.assignment_question_id);
                      }
                    });
                  });

                  const allExpanded = allParentIds.every(id => expandedParents.has(id)) &&
                                     allQuestionIds.every(id => expandedQuestions.has(id));

                  if (allExpanded) {
                    // Collapse all - parents and questions
                    setExpandedParents(new Set());
                    setExpandedQuestions(new Set());
                  } else {
                    // Expand all - parents and questions
                    setExpandedParents(new Set(allParentIds));
                    setExpandedQuestions(new Set(allQuestionIds));
                  }
                }}
                aria-label={(() => {
                  const allParentIds: string[] = [];
                  sections?.forEach((section: any) => {
                    section.questions?.forEach((q: any) => {
                      if (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) {
                        allParentIds.push(q.assignment_question_id);
                      }
                    });
                  });
                  const allExpanded = allParentIds.every(id => expandedParents.has(id));
                  return allExpanded ? 'Collapse all sections' : 'Expand all sections';
                })()}
                sx={{
                  color: 'primary.main',
                  bgcolor: 'primary.lighter',
                  '&:hover': { bgcolor: 'primary.light', transform: 'scale(1.05)' },
                  '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                  transition: 'all 0.2s'
                }}
              >
              <ExpandMore
                sx={{
                  transform: (() => {
                    const allParentIds: string[] = [];
                    sections?.forEach((section: any) => {
                      section.questions?.forEach((q: any) => {
                        if (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) {
                          allParentIds.push(q.assignment_question_id);
                        }
                      });
                    });
                    const allExpanded = allParentIds.every(id => expandedParents.has(id));
                    return allExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
                  })(),
                  transition: 'transform 0.2s'
                }}
              />
            </IconButton>
          </Tooltip>
          </Box>

          {sections?.map((section: any) => (
            <Accordion
              key={section.section_id}
              defaultExpanded={false}
              sx={{ mb: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {section.section_name}
                  </Typography>
                  <Chip
                    label={`${section.questions?.length || 0} Questions`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${section.section_score?.toFixed(1)}/${section.section_max_score}`}
                    color="primary"
                    size="small"
                  />
                  {/* Expand/Collapse All icon button */}
                  <Tooltip title={`${section.questions?.some((q: any) =>
                    (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) &&
                    expandedParents.has(q.assignment_question_id)
                  ) ? 'Collapse All' : 'Expand All'}`}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion toggle
                        const hasAnyExpanded = section.questions?.some((q: any) =>
                          (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) &&
                          expandedParents.has(q.assignment_question_id)
                        );
                        if (hasAnyExpanded) {
                          collapseAllInSection(section.questions);
                        } else {
                          expandAllInSection(section.questions);
                        }
                      }}
                      aria-label={`${section.questions?.some((q: any) =>
                        (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) &&
                        expandedParents.has(q.assignment_question_id)
                      ) ? 'Collapse' : 'Expand'} all questions in ${section.section_name}`}
                      sx={{
                        color: 'primary.main',
                        bgcolor: 'primary.lighter',
                        '&:hover': { bgcolor: 'primary.light', transform: 'scale(1.05)' },
                        '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                        transition: 'all 0.2s'
                      }}
                    >
                    <ExpandMore
                      sx={{
                        transform: section.questions?.some((q: any) =>
                          (q.question_type?.includes('_set') || q.question_type?.includes('comprehension_parent')) &&
                          expandedParents.has(q.assignment_question_id)
                        ) ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                    />
                  </IconButton>
                </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {(() => {
                  if (!section.questions || section.questions.length === 0) return null;

                  // Group questions by parent_question_id
                  const parentQuestions: any[] = [];
                  const childQuestionsByParent: { [key: string]: any[] } = {};
                  const standaloneQuestions: any[] = [];

                  console.log('🔍 DEBUG: Grouping questions in section:', section.section_name);
                  console.log('   Total questions:', section.questions?.length);

                  section.questions.forEach((question: any, idx: number) => {
                    console.log(`   Q${idx + 1}: ${question.question_type} | parent_id: ${question.parent_question_id || 'none'} | aq_id: ${question.assignment_question_id}`);

                    if (question.parent_question_id) {
                      // This is a child question
                      if (!childQuestionsByParent[question.parent_question_id]) {
                        childQuestionsByParent[question.parent_question_id] = [];
                      }
                      childQuestionsByParent[question.parent_question_id].push(question);
                      console.log(`      → Child of parent: ${question.parent_question_id}`);
                    } else if (
                      question.question_type?.includes('_set') ||
                      question.question_type?.includes('comprehension_parent')
                    ) {
                      // This is a parent question
                      parentQuestions.push(question);
                      console.log(`      → Parent question`);
                    } else {
                      // Standalone question
                      standaloneQuestions.push(question);
                      console.log(`      → Standalone question`);
                    }
                  });

                  console.log('   Parent questions:', parentQuestions.length);
                  console.log('   Standalone questions:', standaloneQuestions.length);
                  console.log('   Child questions by parent:', Object.keys(childQuestionsByParent).length);
                  Object.keys(childQuestionsByParent).forEach(parentId => {
                    console.log(`      Parent ${parentId}: ${childQuestionsByParent[parentId].length} children`);
                  });

                  let questionIndex = 0;

                  return (
                    <>
                      {/* Render parent questions with their children */}
                      {parentQuestions.map((parentQuestion: any) => {
                        const childQuestions = childQuestionsByParent[parentQuestion.assignment_question_id] || [];
                        const parentId = parentQuestion.assignment_question_id;
                        const isExpanded = expandedParents.has(parentId);

                        return (
                          <Box key={parentId} sx={{ mb: 3 }}>
                            {/* Parent Question Display */}
                            <ParentQuestionDisplay
                              question={parentQuestion}
                              isExpanded={isExpanded}
                              onToggle={() => toggleParentExpanded(parentId)}
                              childCount={childQuestions.length}
                            />

                            {/* Child Questions - Only show when expanded */}
                            {isExpanded && childQuestions.length > 0 && (
                              <Box
                                sx={{
                                  ml: 4,
                                  mt: 3,
                                  pl: 3,
                                  borderLeft: '3px solid',
                                  borderColor: parentQuestion.question_type?.includes('listening') ? '#2196f3' : '#9c27b0',
                                  bgcolor: parentQuestion.question_type?.includes('listening') ? '#f5f9ff' : '#faf5ff'
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 2,
                                    pt: 2,
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  Questions based on the {parentQuestion.question_type?.includes('listening') ? 'audio' : 'passage'}:
                                </Typography>
                                {childQuestions.map((question: any) => {
                                  const currentIndex = questionIndex++;
                                  const questionId = question.assignment_question_id;
                                  return (
                                    <QuestionAnalysisCard
                                      key={questionId}
                                      question={question}
                                      index={currentIndex}
                                      expanded={expandedQuestions.has(questionId)}
                                      onToggle={() => {
                                        setExpandedQuestions(prev => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(questionId)) {
                                            newSet.delete(questionId);
                                          } else {
                                            newSet.add(questionId);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        );
                      })}

                      {/* Render standalone questions */}
                      {standaloneQuestions.map((question: any) => {
                        const currentIndex = questionIndex++;
                        const questionId = question.assignment_question_id;
                        return (
                          <QuestionAnalysisCard
                            key={questionId}
                            question={question}
                            index={currentIndex}
                            expanded={expandedQuestions.has(questionId)}
                            onToggle={() => {
                              setExpandedQuestions(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(questionId)) {
                                  newSet.delete(questionId);
                                } else {
                                  newSet.add(questionId);
                                }
                                return newSet;
                              });
                            }}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Button onClick={onClose} variant="contained" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PremiumAssignmentResults;
